import { getRubrics, getSystemRubrics, deleteSingleRubric } from "@/api";

export const criteriaChecker = ({ scaleType, criterias }) => {
  // ** TODO validate grading criteria **
  // Input should be text with no special characters
  // Input should not contain numbers
  const maxTitleLength = 255;
  const maxTitleLengthError = `The criteria name is too long. The maximum is ${maxTitleLength} characters.`;

  let proceed = true;
  let error = "";

  criterias.forEach((criteria) => {
    if (
      scaleType === "Default" &&
      !criteria?.criteriaGrades?.[0]?.description
    ) {
      proceed = false;
      error = "Criteria description is required.";
    }

    if (
      (scaleType === "Simple" || scaleType === "Default") &&
      criteria?.criteriaGrades?.[0]?.description.length > 255
    ) {
      proceed = false;
      error = maxTitleLengthError;
    }

    if (scaleType === "Advanced" && criteria?.name?.length > 255) {
      proceed = false;
      error = maxTitleLengthError;
    }

    if (scaleType === "Simple" || scaleType === "Advanced") {
      criteria.criteriaGrades.forEach((grade) => {
        if (!grade.description || grade.maxGrade < 0) {
          proceed = false;
          error =
            "Missing criteria details. Ensure all criteria fields are filled out.";
        }
      });

      // Check for duplicate scores within the same criterion
      const scores = criteria.criteriaGrades.map((grade) => grade.maxGrade);
      const uniqueScores = new Set(scores);
      if (scores.length !== uniqueScores.size) {
        proceed = false;
        error =
          "Duplicate scores are not allowed within a single criterion. Please ensure each score level is unique.";
      }
    }
  });

  return { canCreateRubric: proceed, rubricError: error };
};

export const MAX_COUNT_WORDS = 25000;
const getWordCount = (str) =>
  str.trim().length ? str.trim().split(" ").length : 0;
export const getWordsCountOfAllText = (criteriasArr) => {
  return criteriasArr.reduce((acc, criteria) => {
    return (
      acc +
      getWordCount(criteria.point) +
      criteria.scales.reduce((acc, scale) => {
        return acc + getWordCount(scale.description);
      }, 0)
    );
  }, 0);
};

/**
 * Fetch user's rubrics or shared rubrics
 * @param {boolean} my - If true, fetch user's own rubrics, else fetch shared
 * @returns {Promise<Array>} Array of rubrics
 */
export async function fetchRubrics(type = "platform") {
  try {
    let response;
    switch (type) {
      case "platform":
        response = await getSystemRubrics();
        break;
      case "my":
        response = await getRubrics(true);
        break;
      case "shared":
        response = await getRubrics(false);
        break;
    }

    return response.data || [];
  } catch (error) {
    console.error("Error fetching rubrics", type, ":", error);
    throw error;
  }
}

/**
 * Delete single or multiple rubrics
 * @param {number|Array<number>} ids - Single rubric ID or array of IDs
 * @returns {Promise<void>}
 */
export async function deleteRubrics(ids) {
  const idsArray = Array.isArray(ids) ? ids : [ids];

  try {
    await Promise.all(idsArray.map((id) => deleteSingleRubric(id)));
  } catch (error) {
    console.error("Error deleting rubrics:", error);
    throw error;
  }
}

/**
 * Filter rubrics based on filter criteria
 * @param {Array} rubrics - Array of rubrics to filter
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered rubrics
 */
export function filterRubrics(rubrics, filters) {
  return rubrics.filter((rubric) => {
    // Search query filter
    if (
      filters.name &&
      !rubric.name.toLowerCase().includes(filters.name.toLowerCase())
    ) {
      return false;
    }

    // Grade level filter
    if (filters.gradeLevel && rubric.gradeLevel !== filters.gradeLevel) {
      return false;
    }

    // Intensity filter
    if (filters.intensity && rubric.intensity !== filters.intensity) {
      return false;
    }

    // Language filter
    if (filters.languageId && rubric.language?.id !== filters.languageId) {
      return false;
    }

    // Created date range filter
    if (filters.createdAt) {
      const rubricTimestamp = new Date(rubric.createdAt).getTime();

      // Check if rubric date is within the range
      if (filters.createdAt.from && rubricTimestamp < filters.createdAt.from) {
        return false;
      }

      if (filters.createdAt.to && rubricTimestamp > filters.createdAt.to) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Sort rubrics based on sort criteria
 * @param {Array} rubrics - Array of rubrics to sort
 * @param {string} sortBy - Field to sort by
 * @param {boolean} sortDesc - Sort descending if true
 * @returns {Array} Sorted rubrics
 */
export function sortRubrics(rubrics, sortBy, sortDesc = false) {
  if (!sortBy) return rubrics;

  const sorted = [...rubrics].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    // Handle string comparisons
    if (typeof aVal === "string") {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    // Handle date comparisons
    if (sortBy === "createdAt") {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (aVal < bVal) return sortDesc ? 1 : -1;
    if (aVal > bVal) return sortDesc ? -1 : 1;
    return 0;
  });

  return sorted;
}

/**
 * Paginate rubrics array
 * @param {Array} rubrics - Array of rubrics to paginate
 * @param {number} page - Current page (1-indexed)
 * @param {number} limit - Items per page
 * @returns {Array} Paginated rubrics
 */
export function paginateRubrics(rubrics, page = 1, limit = 10) {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  return rubrics.slice(startIndex, endIndex);
}

export default {
  fetchRubrics,
  deleteRubrics,
  filterRubrics,
  sortRubrics,
  paginateRubrics,
};
